// Shop/Konto: SEPA-Mandat verwalten

import { useEffect, useState } from "react";
import { useAuth } from "@/stores/authStore";
import { supabase } from "@/lib/supabase";
import { useMyMandate, useCreateMandate, useRevokeMandate } from "@/lib/sepa";

const dfmt = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "long", year: "numeric" });

export default function KontoPage() {
  const user = useAuth((s) => s.user);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState<string>("");

  useEffect(() => {
    if (!user) return;
    supabase.from("customers").select("id,display_name").eq("profile_id", user.id).maybeSingle()
      .then(({ data }) => { if (data) { setCustomerId(data.id); setCustomerName(data.display_name); } });
  }, [user]);

  const { data: mandate, isLoading } = useMyMandate();
  const create = useCreateMandate();
  const revoke = useRevokeMandate();

  const [iban, setIban] = useState("");
  const [bic, setBic] = useState("");
  const [holder, setHolder] = useState("");
  const [accept, setAccept] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { setHolder(customerName); }, [customerName]);

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!customerId) return setErr("Konto wird noch geladen");
    if (!accept) return setErr("Bitte SEPA-Mandat-Bedingungen bestätigen");
    try {
      await create.mutateAsync({ customerId, iban, bic, accountHolder: holder });
      setIban(""); setBic(""); setAccept(false);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-brand font-bold text-wappen-700">Mein Konto</h1>

      <section className="card space-y-3">
        <h2 className="font-semibold text-hof-800">SEPA-Lastschrift-Mandat</h2>
        {isLoading ? (
          <p>Lade…</p>
        ) : mandate ? (
          <div className="space-y-3">
            <div className="rounded-xl bg-wiese-50 border border-wiese-200 p-3 space-y-1">
              <div><span className="text-hof-600 text-sm">Mandatsreferenz:</span> <span className="font-mono">{mandate.mandate_ref}</span></div>
              <div><span className="text-hof-600 text-sm">Kontoinhaber:</span> {mandate.account_holder}</div>
              <div><span className="text-hof-600 text-sm">IBAN:</span> <span className="font-mono">DE••• •••• •••• •••• {mandate.iban_last4}</span></div>
              {mandate.bic && <div><span className="text-hof-600 text-sm">BIC:</span> <span className="font-mono">{mandate.bic}</span></div>}
              <div className="text-xs text-hof-500">Erteilt am {dfmt.format(new Date(mandate.signed_at))}</div>
            </div>
            <button onClick={() => {
              if (confirm("Mandat wirklich widerrufen?")) revoke.mutate(mandate.id);
            }} className="btn-danger">Mandat widerrufen</button>
          </div>
        ) : (
          <form onSubmit={onCreate} className="space-y-3">
            <p className="text-sm text-hof-600">
              Mit einem SEPA-Mandat ziehen wir Rechnungsbeträge automatisch von Ihrem Konto ein. Sicher und bequem.
            </p>
            <div>
              <label className="label">Kontoinhaber *</label>
              <input className="input" value={holder} onChange={(e) => setHolder(e.target.value)} required />
            </div>
            <div>
              <label className="label">IBAN *</label>
              <input className="input font-mono" value={iban} onChange={(e) => setIban(e.target.value)} required
                placeholder="DE00 0000 0000 0000 0000 00" autoComplete="off" inputMode="text" />
            </div>
            <div>
              <label className="label">BIC <span className="text-xs text-hof-500">(optional bei DE-IBAN)</span></label>
              <input className="input font-mono" value={bic} onChange={(e) => setBic(e.target.value)} autoComplete="off" />
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-1 w-5 h-5" required />
              <span className="text-sm text-hof-700">
                Ich ermächtige Hof Sirges, Zahlungen von meinem Konto mittels Lastschrift einzuziehen.
                Zugleich weise ich mein Kreditinstitut an, die von Hof Sirges auf mein Konto gezogenen
                Lastschriften einzulösen. Hinweis: Ich kann innerhalb von acht Wochen, beginnend mit
                dem Belastungsdatum, die Erstattung des belasteten Betrags verlangen.
              </span>
            </label>
            {err && <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-red-800 text-sm">{err}</div>}
            <button type="submit" disabled={create.isPending} className="btn-primary w-full">
              {create.isPending ? "Speichern…" : "SEPA-Mandat erteilen"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
